/*
Copyright 2022 Fondazione Bruno Kessler
Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
except in compliance with the Licence.

You may obtain a copy of the Licence at:
http://ec.europa.eu/idabc/eupl.html

Unless required by applicable law or agreed to in writing, software distributed under 
the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF 
ANY KIND, either express or implied. See the Licence for the specific language 
governing permissions and limitations under the Licence.
*/
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from "@angular/core";
import {UserData} from "@app/models/user-data";
import {CommonState} from "@app/store/common/common.state";
import {ModalController, ToastController} from "@ionic/angular";
import {Store} from "@ngxs/store";
import {
  CreateTCGroup,
  PersonalData,
  SaluteHelperService,
  TCParticipant,
  VideoChatService,
} from "@trec/api";
import {Subscription} from "rxjs";

@Component({
  selector: "c3-modal-add-teleconsulto",
  templateUrl: "./modal-add-teleconsulto.component.html",
  styleUrls: ["./modal-add-teleconsulto.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ModalAddTeleconsultoComponent implements OnInit {
  loading = false;
  patientPersonalData?: PersonalData | null;
  userData?: UserData | null;
  name: string = "";
  participantsList: any = [];
  selectedParticipants: {
    trecUserId: string;
    isPatient?: boolean;
    name: string;
    surname: string;
    email?: string;
  }[] = [];
  creationValue: boolean = true;
  trecUserId;
  patientId;

  private readonly subscr = new Subscription();
  constructor(
    private cd: ChangeDetectorRef,
    private modalController: ModalController,
    private saluteHelperService: SaluteHelperService,
    private toastController: ToastController,
    private store: Store,
    private videoChatService: VideoChatService
  ) { }

  ngOnInit(): void {
    this.subscr.add(
      this.store.select(CommonState.loggedUserData).subscribe((userData) => {
        this.userData = userData;

        this.cd.markForCheck();
      })
    );
    this.subscr.add(
      this.store.select(CommonState.patientPersonalData).subscribe((patientPersonalData) => {
        this.patientPersonalData = patientPersonalData;

        this.cd.markForCheck();
      })
    );

    this.patientId = this.store.selectSnapshot(CommonState.patientId);
    this.trecUserId = this.store.selectSnapshot(CommonState.userId);

    this.getPartecipantList();
  }

  async getPartecipantList() {
    //this.loading = true;
    this.saluteHelperService.getListaMedici().subscribe((data) => {
      this.participantsList = data;
      /*for (let i = 0; i < data.length; i++) {
        for (let j = 0; j < this.filteredPhysician.length; j++) {
          if (data[i].codiceFiscale === this.filteredPhysician[j]) {
            this.participantsList.push(data[i]);
          }
        }
      }*/
      this.loading = false;
      this.cd.detectChanges();
    });

    for (let i = 0; i < this.participantsList.length; i++) {
      this.participantsList[i].checked = false;
    }
  }

  async updateParticipants(event: any, participant: any) {
    if (event.target.checked) {
      if (!participant.workEmail) {
        event.target.checked = false;
        const toast = await this.toastController.create({
          header: "Attenzione",
          message: "L'utente non ha un indirizzo email valido",
          duration: 3000,
          color: "warning",
          position: "bottom",
        });
        await toast.present();
        return;
      }
      this.selectedParticipants.push({
        name: participant.name,
        surname: participant.surname,
        trecUserId: participant.id,
        email: participant.workEmail,
      });
    } else {
      this.removeParticipants(participant.id);
      this.selectedParticipants = this.selectedParticipants.filter(
        (p) => p.trecUserId !== participant.id
      );
    }
    this.checkSelection();
  }

  //Removes checkbox from array when you uncheck it
  removeParticipants(partecipantId) {
    return this.selectedParticipants.findIndex((category) => {
      return category.trecUserId === partecipantId;
    });
  }

  creaTeam() {
    this.selectedParticipants.push({
      trecUserId: this.userData?.personalData?.trecUserId!,
      isPatient: false,
      name: this.userData?.personalData?.name!,
      surname: this.userData?.personalData?.surname!,
      email: this.userData?.personalData?.workEmail!,
    });
    for (let i = 0; i < this.selectedParticipants.length; i++) {
      this.selectedParticipants[i].isPatient = false;
      delete (this.selectedParticipants[i] as any).checked;
    }
    let physicians = this.selectedParticipants;
    let tcParticipant: TCParticipant = {
      groupName: this.name,
      creatorId: this.trecUserId,
      physicians: physicians,
    };
    let createTCGroup: CreateTCGroup = {
      patientId: this.patientId,
      patientName: this.patientPersonalData?.name!,
      patientSurname: this.patientPersonalData?.surname!,
      physiciansGroups: tcParticipant,
    };
    this.videoChatService.createTCGroup(createTCGroup).subscribe(
      (data) => {
        this.successToast();
      },
      (err) => {
        console.error(err);
        let error = err.toString();
        error = error.substring(error.indexOf(":") + 1);
        console.error(error);
        this.errorToast(error);
      }
    );
  }

  closeModal() {
    const newTeam = {
      team: this.selectedParticipants,
      name: this.name,
    };
    this.modalController.dismiss(newTeam);
  }

  async errorToast(err: any) {
    const toast = await this.toastController.create({
      header: "Errore",
      message: err,
      duration: 3000,
      color: "danger",
      position: "bottom",
    });
    toast.present();
    this.closeModal();
  }

  async successToast() {
    const toast = await this.toastController.create({
      header: "Ok",
      message: "Team creato con successo",
      duration: 3000,
      color: "success",
      position: "bottom",
    });
    toast.present();
    this.closeModal();
  }

  checkSelection() {
    if (
      this.selectedParticipants.length > 0 &&
      this.name !== "" &&
      this.selectedParticipants.length < 6
    ) {
      this.creationValue = false;
    } else {
      this.creationValue = true;
    }
  }
}
